import React, { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import {
  APP_BG,
  APP_BORDER,
  APP_SEMANTIC,
} from '@/constant/theme/colors.constant';
import { Text } from '@/components/atoms/AppText';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import { tid } from '@/utils/testId';
import { DATE_TIME_PICKER_TEST_IDS as TID } from '@/constant/testIds/dateTimePicker.testIds';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

interface TimeDropdownProps {
  testId?: string;
  value: string;
  onChange: (val: string) => void;
  options: string[];
  timezone: string;
  selectedDate: Dayjs | null;
  type: 'hour' | 'minute';
  currentHourSelection?: string;
}

const isTimeDisabled = (
  hour: string,
  minute: string,
  timezone: string,
  selectedDate: Dayjs | null,
  type: 'hour' | 'minute',
  currentHourSelection?: string,
): boolean => {
  if (!selectedDate || !selectedDate.isValid()) return true;

  const now = dayjs().tz(timezone);
  const isToday = selectedDate.isSame(now, 'day');

  if (!isToday) return false;

  const currentHour = now.hour();
  const currentMinute = now.minute();

  const h = parseInt(hour, 10);
  const m = parseInt(minute, 10);

  if (type === 'hour') {
    return h > currentHour;
  }
  const selectedH = parseInt(currentHourSelection || '0', 10);
  if (selectedH > currentHour) return true;
  if (selectedH === currentHour) return m > currentMinute;
  return false;
};

export const TimeDropdown: React.FC<TimeDropdownProps> = ({
  testId,
  value,
  onChange,
  options,
  timezone,
  selectedDate,
  type,
  currentHourSelection,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isDateMissing = !selectedDate || !selectedDate.isValid();

  return (
    <div className={isOpen ? 'relative z-[60]' : 'relative'}>
      {/* TRIGGER */}
      <button
        type="button"
        data-testid={testId ?? ''}
        disabled={isDateMissing}
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-14 h-8 flex items-center justify-center
          text-[11px] font-semibold
          rounded-lg border transition-all
          ${
            isDateMissing
              ? `${APP_BG.muted} ${APP_SEMANTIC.neutral.text} ${APP_BORDER.default} cursor-not-allowed opacity-50`
              : `${APP_BG.base} ${APP_SEMANTIC.neutral.text} ${APP_BORDER.default} hover:bg-primary/10`
          }
        `}
      >
        <span className="leading-none tabular-nums">{value || '00'}</span>
      </button>

      {/* DROPDOWN */}
      {isOpen && !isDateMissing && (
        <>
          {/* BACKDROP */}
          <div
            className="fixed inset-0 z-30"
            onClick={() => setIsOpen(false)}
          />

          <ul
            className={`
              absolute left-0 z-[70] w-20 mt-1 max-h-44 overflow-y-auto
              rounded-lg border shadow-xl

              ${APP_BG.base}
              ${APP_BORDER.default}
            `}
          >
            {options.map((opt, i) => {
              const isDisabled = isTimeDisabled(
                type === 'hour' ? opt : currentHourSelection || '0',
                type === 'minute' ? opt : '0',
                timezone,
                selectedDate,
                type,
                currentHourSelection,
              );

              const isSelected = value === opt && !isDisabled;

              return (
                <li
                  key={opt}
                  data-testid={tid(TID.TIME_DROPDOWN_OPTION(opt, "", testId || ""))}
                  onClick={() => {
                    if (!isDisabled) {
                      onChange(opt);
                      setIsOpen(false);
                    }
                  }}
                  className={`
                    relative px-3 py-1.5 text-[11px] flex items-center justify-between
                    transition-all duration-150 border-b last:border-b-0 ${APP_BORDER.filterRow}

                    ${
                      isDisabled
                        ? `${APP_SEMANTIC.neutral.text} opacity-40 cursor-not-allowed`
                        : `cursor-pointer ${APP_SEMANTIC.neutral.text} hover:bg-primary/10 dark:hover:bg-white/10`
                    }

                    ${
                      isSelected
                        ? 'bg-gradient-to-r from-primary via-secondary to-tertiary text-white font-semibold'
                        : ''
                    }
                  `}
                >
                  <Text as="span" size="base" color="inherit">
                    {opt}
                  </Text>

                  {/* ACTIVE DOT */}
                  {isSelected && (
                    <div className="absolute right-2">
                      <div className="relative">
                        <div className="h-1.5 w-1.5 rounded-full bg-white" />
                        <div className="absolute inset-0 h-1.5 w-1.5 rounded-full bg-white animate-ping opacity-60" />
                      </div>
                    </div>
                  )}

                  {isDisabled && (
                    <Text
                      as="span"
                      size="2xs"
                      color="inherit"
                      className="uppercase opacity-50"
                    >
                      {ORGANISM_PROPS.TIME_DROPDOWN.FUTURE_LABEL}
                    </Text>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
};
